import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

/** Query param that carries a tab id into any tabbed settings page. */
export const TAB_PARAM = 'tab';

/** Minimum a tab must expose to be addressable by a deep link. */
interface DeepLinkableTab {
    id?: string;
}

/**
 * Opens the tab named by a `?tab=<id>` query param.
 *
 * Tabs are addressed by id rather than by index because tab lists are rarely fixed — the provider
 * settings list, for one, varies per provider and by fulfillment mode — so a positional link would
 * land on the wrong tab, or off the end of the list. An unknown or absent id leaves the current
 * selection alone.
 *
 * Fires once per mount, and leaves the param in the URL so the link survives a reload and can be
 * shared. The guard matters: tab arrays are usually rebuilt on every render, so without it this
 * would re-run and snap the user back to the linked tab after every manual switch.
 *
 * @param tabs   the tab list as rendered, in display order
 * @param onFound called with the index of the linked tab
 */
export const useTabDeepLink = (tabs: DeepLinkableTab[] | undefined, onFound: (index: number) => void): void => {
    const [searchParams] = useSearchParams();
    const handledRef = useRef(false);

    // Kept in a ref so a caller passing an inline arrow does not re-trigger the effect.
    const onFoundRef = useRef(onFound);
    onFoundRef.current = onFound;

    useEffect(() => {
        if (handledRef.current) return;

        const requestedTab = searchParams.get(TAB_PARAM);

        if (!requestedTab) return;

        // The list is empty on the first renders while the page's data loads; wait for it rather
        // than giving up on a tab that is about to appear.
        if (!tabs?.length) return;

        handledRef.current = true;

        const index = tabs.findIndex((tab) => tab?.id === requestedTab);

        if (index >= 0) onFoundRef.current(index);
    }, [searchParams, tabs]);
};

/** Appends `?tab=<id>` to a path, for linking into a specific tab from elsewhere. */
export const withTabParam = (path: string, tabId: string): string =>
    `${path}${path.includes('?') ? '&' : '?'}${TAB_PARAM}=${tabId}`;
